import { useState, useEffect, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Search, RefreshCw, AlertCircle, CheckCircle,
  BookOpen, Trash2, Eye, Star, Users, DollarSign, Clock, MapPin, Plus, Copy
} from 'lucide-react'
import toast from 'react-hot-toast'
import { 
  courseService, 
  Course, 
  CourseStats, 
  formatCourseType, 
  formatDifficulty, 
  formatPrice, 
  formatDuration,
  formatRelativeTime,
  getCourseSchool
} from '../services/courseService'
import { yogaSchoolService, YogaSchool } from '../services/yogaSchoolService'
import SearchInput from '../components/SearchInput'
import AdminPagination from '../components/AdminPagination'
import { useAuth } from '../contexts/AuthContext'
import CourseScheduleDates from '../components/CourseScheduleDates'
import { hasCourseScheduleSection } from '../utils/courseSchedule'

const Courses = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canManage = hasPermission('courses.manage')
  
  // Debug: Log URL parameters immediately
  console.log('Component mounted. URL schoolId:', searchParams.get('schoolId'))
  
  const [courses, setCourses] = useState<Course[]>([])
  const [schools, setSchools] = useState<YogaSchool[]>([])
  const [stats, setStats] = useState<CourseStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [difficultyFilter, setDifficultyFilter] = useState('ALL')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [featuredFilter, setFeaturedFilter] = useState('ALL')
  const [schoolIdFilter, setSchoolIdFilter] = useState(searchParams.get('schoolId') || 'ALL')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [selectedCourse, _setSelectedCourse] = useState<Course | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null)
  const [showToggleModal, setShowToggleModal] = useState(false)
  const [courseToToggle, setCourseToToggle] = useState<Course | null>(null)
  const [showFeatureModal, setShowFeatureModal] = useState(false)
  const [courseToFeature, setCourseToFeature] = useState<Course | null>(null)

  // Refs for latest values to avoid stale closures
  const searchTermRef = useRef(searchTerm)
  const typeFilterRef = useRef(typeFilter)
  const difficultyFilterRef = useRef(difficultyFilter)
  const statusFilterRef = useRef(statusFilter)
  const featuredFilterRef = useRef(featuredFilter)
  const schoolIdFilterRef = useRef(schoolIdFilter)

  // Update refs when state changes
  useEffect(() => {
    searchTermRef.current = searchTerm
    typeFilterRef.current = typeFilter
    difficultyFilterRef.current = difficultyFilter
    statusFilterRef.current = statusFilter
    featuredFilterRef.current = featuredFilter
    schoolIdFilterRef.current = schoolIdFilter
  }, [searchTerm, typeFilter, difficultyFilter, statusFilter, featuredFilter, schoolIdFilter])

  const fetchCourses = useCallback(async (page: number = 1, search?: string, type?: string, difficulty?: string, status?: string, featured?: string, schoolId?: string) => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {
        page,
        limit: 12,
        search: search || undefined,
        type: type !== 'ALL' ? type : undefined,
        difficulty: difficulty !== 'ALL' ? difficulty : undefined,
        isActive: status !== 'ALL' ? status === 'true' : undefined,
        featured: featured !== 'ALL' ? featured === 'true' : undefined,
        schoolId: schoolId !== 'ALL' ? schoolId : undefined,
        sortBy: 'createdAt' as const,
        sortOrder: 'desc' as const
      }

      console.log('Fetching courses with params:', params)
      const response = await courseService.getCourses(params)
      
      if (response.success && response.data) {
        setCourses(response.data.courses)
        const pagination = response.data.pagination as {
          pages?: number
          total?: number
        }
        setTotalPages(Math.max(1, pagination.pages ?? 1))
        setTotalItems(typeof pagination.total === 'number' ? pagination.total : 0)
        setCurrentPage(page)
      }
    } catch (error: any) {
      setError(error.message || 'Failed to fetch courses')
    } finally {
      setLoading(false)
    }
  }, [])

  const goToCoursesPage = useCallback(
    (page: number) => {
      fetchCourses(
        page,
        searchTermRef.current,
        typeFilterRef.current,
        difficultyFilterRef.current,
        statusFilterRef.current,
        featuredFilterRef.current,
        schoolIdFilterRef.current,
      )
    },
    [fetchCourses],
  )

  const fetchStats = useCallback(async (schoolId?: string) => {
    try {
      const response = await courseService.getCourseStats(schoolId)
      if (response.success && response.data && response.data.stats) {
        setStats(response.data.stats)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }, [])

  const fetchSchools = useCallback(async () => {
    try {
      const response = await yogaSchoolService.getYogaSchools({ page: 1, limit: 100 })
      if (response.success && response.data && response.data.schools) {
        setSchools(response.data.schools)
      }
    } catch (error) {
      console.error('Error fetching schools:', error)
    }
  }, [])

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCourses(1, searchTermRef.current, typeFilterRef.current, difficultyFilterRef.current, statusFilterRef.current, featuredFilterRef.current, schoolIdFilterRef.current)
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchTerm, typeFilter, difficultyFilter, statusFilter, featuredFilter, schoolIdFilter, fetchCourses])

  // Search input change handler
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
  }

  // Fetch stats when schoolIdFilter changes
  useEffect(() => {
    const schoolIdToUse = schoolIdFilter !== 'ALL' ? schoolIdFilter : undefined
    fetchStats(schoolIdToUse)
  }, [schoolIdFilter, fetchStats])

  useEffect(() => {
    fetchSchools()
  }, [])

  // Fallback: Fetch courses on mount if not already fetched
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (courses.length === 0 && !loading) {
        console.log('Fallback: Fetching courses on mount')
        fetchCourses(1, searchTermRef.current, typeFilterRef.current, difficultyFilterRef.current, statusFilterRef.current, featuredFilterRef.current, schoolIdFilterRef.current)
      }
    }, 100)
    
    return () => clearTimeout(timeoutId)
  }, [])

  // Handle URL parameter changes and initial load
  useEffect(() => {
    const schoolId = searchParams.get('schoolId')
    console.log('URL schoolId parameter:', schoolId)
    console.log('Current schoolIdFilter:', schoolIdFilter)
    
    if (schoolId && schoolId !== schoolIdFilter) {
      console.log('Setting schoolIdFilter to:', schoolId)
      setSchoolIdFilter(schoolId)
    }
    // Always fetch courses and stats on mount and when URL changes
    const filterToUse = schoolId || schoolIdFilterRef.current
    console.log('Fetching courses with schoolId:', filterToUse)
    fetchCourses(1, searchTermRef.current, typeFilterRef.current, difficultyFilterRef.current, statusFilterRef.current, featuredFilterRef.current, filterToUse)
    // Fetch stats with the schoolId from URL or current filter
    const statsSchoolId = schoolId && schoolId !== 'ALL' ? schoolId : (schoolIdFilterRef.current !== 'ALL' ? schoolIdFilterRef.current : undefined)
    fetchStats(statsSchoolId)
  }, [searchParams, fetchCourses, fetchStats])

  // Fetch courses when schoolIdFilter changes (from dropdown)
  useEffect(() => {
    if (schoolIdFilter !== 'ALL' && !searchParams.get('schoolId')) {
      fetchCourses(1, searchTermRef.current, typeFilterRef.current, difficultyFilterRef.current, statusFilterRef.current, featuredFilterRef.current, schoolIdFilter)
    }
  }, [schoolIdFilter])

  const handleCourseAction = (action: string, course: Course) => {
    switch (action) {
      case 'view':
        navigate(`/admin/courses/${course._id}`)
        break
      case 'toggle':
        setCourseToToggle(course)
        setShowToggleModal(true)
        break
      case 'feature':
        setCourseToFeature(course)
        setShowFeatureModal(true)
        break
      case 'delete':
        setCourseToDelete(course)
        setShowDeleteModal(true)
        break
      case 'duplicate':
        handleDuplicate(course)
        break
    }
  }

  const handleDuplicate = async (course: Course) => {
    if (!canManage) {
      toast.error('Not enough permissions')
      return
    }
    try {
      setActionLoading(course._id)
      const response = await courseService.duplicateCourse(course._id)
      if (response?.success) {
        toast.success('Course duplicated successfully')
        await fetchCourses(currentPage, searchTermRef.current, typeFilterRef.current, difficultyFilterRef.current, statusFilterRef.current, featuredFilterRef.current, schoolIdFilterRef.current)
      } else {
        toast.error(response?.message || 'Failed to duplicate course')
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to duplicate course')
    } finally {
      setActionLoading(null)
    }
  }

  const handleToggleConfirm = async () => {
    if (!courseToToggle) return
    
    try {
      setActionLoading(courseToToggle._id)
      await courseService.toggleCourseStatus(courseToToggle._id, !courseToToggle.isActive)
      await fetchCourses(currentPage, searchTermRef.current, typeFilterRef.current, difficultyFilterRef.current, statusFilterRef.current, featuredFilterRef.current, schoolIdFilterRef.current)
      setShowToggleModal(false)
      setCourseToToggle(null)
    } catch (error: any) {
      setError(error.message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleFeatureConfirm = async () => {
    if (!courseToFeature) return
    
    try {
      setActionLoading(courseToFeature._id)
      await courseService.toggleCourseFeatured(courseToFeature._id, !courseToFeature.isFeatured)
      await fetchCourses(currentPage, searchTermRef.current, typeFilterRef.current, difficultyFilterRef.current, statusFilterRef.current, featuredFilterRef.current, schoolIdFilterRef.current)
      setShowFeatureModal(false)
      setCourseToFeature(null)
    } catch (error: any) {
      setError(error.message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!courseToDelete) return
    
    try {
      setActionLoading(courseToDelete._id)
      await courseService.deleteCourse(courseToDelete._id)
      await fetchCourses(currentPage, searchTermRef.current, typeFilterRef.current, difficultyFilterRef.current, statusFilterRef.current, featuredFilterRef.current, schoolIdFilterRef.current)
      setShowDeleteModal(false)
      setCourseToDelete(null)
    } catch (error: any) {
      setError(error.message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
    setCourseToDelete(null)
  }

  const handleToggleCancel = () => {
    setShowToggleModal(false)
    setCourseToToggle(null)
  }

  const handleFeatureCancel = () => {
    setShowFeatureModal(false)
    setCourseToFeature(null)
  }


  const getTypeBadge = (type: Course['type']) => {
    const colors = {
      COURSE: 'bg-blue-100 text-blue-800',
      RETREAT: 'bg-green-100 text-green-800',
      WORKSHOP: 'bg-purple-100 text-purple-800',
      TRAINING: 'bg-orange-100 text-orange-800',
      CLASS: 'bg-gray-100 text-gray-800'
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type]}`}>
        {formatCourseType(type)}
      </span>
    )
  }

  const getDifficultyBadge = (difficulty: Course['difficulty']) => {
    const colors = {
      BEGINNER: 'bg-green-100 text-green-800',
      INTERMEDIATE: 'bg-yellow-100 text-yellow-800',
      ADVANCED: 'bg-red-100 text-red-800',
      ALL_LEVELS: 'bg-blue-100 text-blue-800'
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[difficulty]}`}>
        {formatDifficulty(difficulty)}
      </span>
    )
  }

  const getStatusBadge = (course: Course) => {
    if (!course.isActive) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>
    }
    if (course.isFeatured) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Featured</span>
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Courses</h1>
          <p className="text-gray-600">Manage all yoga courses and programs</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{courses.length} courses</span>
          {canManage && (
            <button
              onClick={() =>
                navigate(
                  schoolIdFilter !== 'ALL'
                    ? `/admin/courses/new?schoolId=${schoolIdFilter}`
                    : '/admin/courses/new',
                )
              }
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Add Course
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Courses', value: stats.totalCourses, color: 'bg-blue-500', icon: BookOpen },
            { label: 'Active Courses', value: stats.activeCourses, color: 'bg-green-500', icon: CheckCircle },
            { label: 'Featured Courses', value: stats.featuredCourses, color: 'bg-yellow-500', icon: Star },
            { label: 'Total Bookings', value: stats.totalBookings, color: 'bg-purple-500', icon: Users }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`p-4 rounded-2xl ${stat.color} shadow-lg`}>
                  <stat.icon className="h-8 w-8 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4 items-end">
          <div className="w-full lg:w-2/3">
            <SearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search courses by name, description..."
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full lg:w-40 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Types</option>
            <option value="COURSE">Course</option>
            <option value="RETREAT">Retreat</option>
            <option value="WORKSHOP">Workshop</option>
            <option value="TRAINING">Training</option>
            <option value="CLASS">Class</option>
          </select>
          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            className="w-full lg:w-40 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Levels</option>
            <option value="BEGINNER">Beginner</option>
            <option value="INTERMEDIATE">Intermediate</option>
            <option value="ADVANCED">Advanced</option>
            <option value="ALL_LEVELS">All Levels</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full lg:w-40 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <select
            value={featuredFilter}
            onChange={(e) => setFeaturedFilter(e.target.value)}
            className="w-full lg:w-40 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Featured</option>
            <option value="true">Featured</option>
            <option value="false">Not Featured</option>
          </select>
          <select
            value={schoolIdFilter}
            onChange={(e) => setSchoolIdFilter(e.target.value)}
            className="w-full lg:w-40 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
          >
            <option value="ALL">All Schools</option>
            {schools.map((school) => (
              <option key={school._id} value={school._id}>
                {school.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => fetchCourses(1, searchTermRef.current, typeFilterRef.current, difficultyFilterRef.current, statusFilterRef.current, featuredFilterRef.current, schoolIdFilterRef.current)}
            disabled={loading}
            className="px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      {/* Courses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-16">
            <div className="text-center">
              <div className="relative mb-4">
                <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Search className="w-5 h-5 text-amber-500 animate-pulse" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Courses</h3>
              <p className="text-gray-500">Please wait while we fetch the latest data...</p>
            </div>
          </div>
        ) : courses.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No courses found</h3>
            <p className="text-gray-500">Try adjusting your search criteria or filters</p>
          </div>
        ) : (
          courses.map((course, index) => (
            <motion.div
              key={course._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-white rounded-xl p-6 shadow-sm border transition-all duration-200 hover:shadow-lg hover:border-amber-300"
            >
              {/* Action buttons */}
              <div className="absolute top-4 right-4 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-all duration-200 bg-white/95 backdrop-blur-sm rounded-lg p-1 shadow-lg border border-gray-200">
                <button
                  onClick={() => handleCourseAction('view', course)}
                  className="p-2 text-gray-600 hover:text-amber-600 transition-colors rounded-md hover:bg-amber-50"
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </button>
                {canManage && (
                  <button
                    onClick={() => handleCourseAction('duplicate', course)}
                    disabled={actionLoading === course._id}
                    className="p-2 text-gray-600 hover:text-blue-600 transition-colors rounded-md hover:bg-blue-50 disabled:opacity-50"
                    title="Duplicate Course"
                  >
                    {actionLoading === course._id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => handleCourseAction('feature', course)}
                  disabled={actionLoading === course._id}
                  className={`p-2 transition-colors rounded-md ${
                    course.isFeatured 
                      ? 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50' 
                      : 'text-gray-600 hover:text-yellow-600 hover:bg-yellow-50'
                  } disabled:opacity-50`}
                  title={course.isFeatured ? 'Remove from Featured' : 'Add to Featured'}
                >
                  <Star className={`w-4 h-4 ${course.isFeatured ? 'fill-current' : ''}`} />
                </button>
                <button
                  onClick={() => handleCourseAction('toggle', course)}
                  disabled={actionLoading === course._id}
                  className={`p-2 transition-colors rounded-md ${
                    course.isActive 
                      ? 'text-gray-600 hover:text-red-600 hover:bg-red-50' 
                      : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                  } disabled:opacity-50`}
                  title={course.isActive ? 'Deactivate' : 'Activate'}
                >
                  {actionLoading === course._id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : course.isActive ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => handleCourseAction('delete', course)}
                  disabled={actionLoading === course._id}
                  className="p-2 text-gray-600 hover:text-red-600 transition-colors rounded-md hover:bg-red-50 disabled:opacity-50"
                  title="Delete Course"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Status badges */}
              <div className="flex items-center space-x-2 mb-4">
                {getStatusBadge(course)}
                {getTypeBadge(course.type)}
                {getDifficultyBadge(course.difficulty)}
              </div>

              {/* Course name */}
              <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                {course.name}
              </h3>

              {/* School info */}
              {(() => {
                const populated = getCourseSchool(course)
                const fallbackId = typeof course.schoolId === 'string' ? course.schoolId : populated?._id
                const fallback = !populated && fallbackId
                  ? schools.find((s) => s._id === fallbackId)
                  : undefined
                const schoolName = populated?.name || fallback?.name
                const schoolCity = populated?.city || fallback?.city
                if (!schoolName) return null
                return (
                  <div className="flex items-start text-sm text-gray-700 mb-3 gap-1.5">
                    <MapPin className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-gray-800 truncate">{schoolName}</div>
                      {schoolCity && (
                        <div className="text-xs text-gray-500 truncate">{schoolCity}</div>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* Course details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-600">
                    <DollarSign className="w-4 h-4 mr-1" />
                    <span>{formatPrice(course.price, course.currency)}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{formatDuration(course.duration)}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-600">
                    <Users className="w-4 h-4 mr-1" />
                    <span>Max {course.maxStudents} students</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Star className="w-4 h-4 mr-1" />
                    <span>{course.rating}/5 ({course.totalReviews})</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                {course.shortDescription || course.description}
              </p>

              {/* Accommodation info */}
              {course.accommodation?.available && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center text-sm text-green-800">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span>Accommodation Available</span>
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    {course.accommodation.rooms.length} room type(s) • 
                    {course.accommodation.mealPlan?.included ? ' Meals included' : ' Meals not included'}
                  </div>
                </div>
              )}

              {/* Bookings */}
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{course.totalBookings} bookings</span>
                <span>{formatRelativeTime(course.createdAt)}</span>
              </div>
            </motion.div>
          ))
        )}
      </div>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        disabled={loading}
        onPageChange={goToCoursesPage}
        className="rounded-2xl shadow-sm"
      />

      {/* Course Detail Modal */}
      {showModal && selectedCourse && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ 
            position: 'fixed',
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100vh', 
            zIndex: 99998,
            margin: 0,
            padding: '16px',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setShowModal(false)}
        >
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="bg-white rounded-2xl p-8 shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto space-y-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4">
              Course Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{selectedCourse.name}</h3>
                <p className="text-gray-600 mb-4">{selectedCourse.description || 'No description'}</p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Type:</span>
                    {getTypeBadge(selectedCourse.type || 'COURSE')}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Difficulty:</span>
                    {getDifficultyBadge(selectedCourse.difficulty || 'ALL_LEVELS')}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Price:</span>
                    <span className="text-sm font-semibold">{formatPrice(selectedCourse.price ?? 0, selectedCourse.currency || 'USD')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Duration:</span>
                    <span className="text-sm">{formatDuration(selectedCourse.duration ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Max Students:</span>
                    <span className="text-sm">{selectedCourse.maxStudents ?? '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Rating:</span>
                    <span className="text-sm">{(selectedCourse.rating ?? 0)}/5 ({(selectedCourse.totalReviews ?? 0)} reviews)</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Schedule</h4>
                {selectedCourse.schedule && hasCourseScheduleSection(selectedCourse.schedule) ? (
                  <div className="space-y-3">
                    {selectedCourse.schedule.startDate && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Fixed start date:</span>
                        <span>{new Date(selectedCourse.schedule.startDate).toLocaleDateString()}</span>
                      </div>
                    )}
                    {selectedCourse.schedule.endDate && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Fixed end date:</span>
                        <span>{new Date(selectedCourse.schedule.endDate).toLocaleDateString()}</span>
                      </div>
                    )}
                    <CourseScheduleDates schedule={selectedCourse.schedule} />
                    {selectedCourse.schedule.timeSlots && selectedCourse.schedule.timeSlots.length > 0 && (
                      <div className="mt-3">
                        <span className="text-sm font-medium text-gray-600">Time Slots:</span>
                        <div className="mt-2 space-y-1">
                          {selectedCourse.schedule.timeSlots.slice(0, 5).map((slot, index) => (
                            <div key={index} className="text-xs text-gray-500">
                              {slot.label ? `${slot.label}: ` : ''}
                              {slot.startTime} – {slot.endTime}
                            </div>
                          ))}
                          {selectedCourse.schedule.timeSlots.length > 5 && (
                            <div className="text-xs text-gray-400">
                              +{selectedCourse.schedule.timeSlots.length - 5} more...
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No schedule information available</p>
                )}


                {selectedCourse.accommodation?.available && (
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Accommodation</h4>
                    <div className="space-y-3">
                      {selectedCourse.accommodation.rooms.map((room, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{room.type || 'Room'}</span>
                            <span className="text-sm font-semibold">{formatPrice(room.price ?? 0, selectedCourse.currency || 'USD')}</span>
                          </div>
                          {(room.capacity != null || room.amenities?.length) ? (
                            <div className="text-xs text-gray-600">
                              {room.capacity != null && `Capacity: ${room.capacity}`}
                              {room.capacity != null && room.amenities?.length ? ' • ' : ''}
                              {room.amenities?.length ? room.amenities.join(', ') : ''}
                            </div>
                          ) : null}
                          {room.description && (
                            <div className="text-xs text-gray-500 mt-1">{room.description}</div>
                          )}
                        </div>
                      ))}
                      {selectedCourse.accommodation.mealPlan && (
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="text-sm font-medium text-green-800">
                            Meal Plan: {selectedCourse.accommodation.mealPlan.type}
                          </div>
                          {selectedCourse.accommodation.mealPlan.description && (
                            <div className="text-xs text-green-600 mt-1">
                              {selectedCourse.accommodation.mealPlan.description}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </div>

            <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Toggle Status Confirmation Modal */}
      {showToggleModal && courseToToggle && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ 
            position: 'fixed',
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100vh', 
            zIndex: 99999,
            margin: 0,
            padding: '16px',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleToggleCancel}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full ${
              courseToToggle.isActive ? 'bg-red-100' : 'bg-green-100'
            }`}>
              {courseToToggle.isActive ? (
                <AlertCircle className="w-6 h-6 text-red-600" />
              ) : (
                <CheckCircle className="w-6 h-6 text-green-600" />
              )}
            </div>
            
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
              {courseToToggle.isActive ? 'Deactivate Course' : 'Activate Course'}
            </h2>
            
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to {courseToToggle.isActive ? 'deactivate' : 'activate'} <span className="font-semibold">{courseToToggle.name}</span>?
              {courseToToggle.isActive && ' This will hide the course from public listings.'}
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleToggleCancel}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleConfirm}
                disabled={actionLoading === courseToToggle._id}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                  courseToToggle.isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {actionLoading === courseToToggle._id ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    {courseToToggle.isActive ? 'Deactivating...' : 'Activating...'}
                  </>
                ) : (
                  courseToToggle.isActive ? 'Deactivate' : 'Activate'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Feature/Unfeature Confirmation Modal */}
      {showFeatureModal && courseToFeature && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ 
            position: 'fixed',
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100vh', 
            zIndex: 99999,
            margin: 0,
            padding: '16px',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleFeatureCancel}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full ${
              courseToFeature.isFeatured ? 'bg-gray-100' : 'bg-yellow-100'
            }`}>
              <Star className={`w-6 h-6 ${courseToFeature.isFeatured ? 'text-gray-600' : 'text-yellow-600'}`} />
            </div>
            
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
              {courseToFeature.isFeatured ? 'Remove from Featured' : 'Add to Featured'}
            </h2>
            
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to {courseToFeature.isFeatured ? 'remove' : 'add'} <span className="font-semibold">{courseToFeature.name}</span> {courseToFeature.isFeatured ? 'from' : 'to'} featured courses?
              {!courseToFeature.isFeatured && ' This will highlight the course in public listings.'}
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleFeatureCancel}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleFeatureConfirm}
                disabled={actionLoading === courseToFeature._id}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                  courseToFeature.isFeatured ? 'bg-gray-600 hover:bg-gray-700' : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                {actionLoading === courseToFeature._id ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    {courseToFeature.isFeatured ? 'Removing...' : 'Adding...'}
                  </>
                ) : (
                  courseToFeature.isFeatured ? 'Remove from Featured' : 'Add to Featured'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && courseToDelete && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ 
            position: 'fixed',
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100vh', 
            zIndex: 99999,
            margin: 0,
            padding: '16px',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleDeleteCancel}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
              Delete Course
            </h2>
            
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to delete <span className="font-semibold">{courseToDelete.name}</span>? 
              This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={actionLoading === courseToDelete._id}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {actionLoading === courseToDelete._id ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Course'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

export default Courses
